<script setup lang="ts">
/**
 * Quick-add type picker — the first step of the overlay.
 *
 * Tenant-aware ordering: on customer subdomains the highest-value action
 * for an analyst is usually attaching a Note to something that already
 * exists, or adding an IOC. Actors and campaigns are mostly CWR-curated
 * and appear visually dimmed + sorted below a divider. On the admin
 * subdomain the CWR-curation order (Actor → Campaign → …) is preserved.
 *
 * Keyboard shortcuts: n/i/a/c/r jump straight to the corresponding form.
 * Clicking a row does the same thing. Escape is handled by the parent
 * overlay via useEscapeKey.
 */
import { computed } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import {
  MessageSquare,
  Fingerprint,
  ShieldAlert,
  Crosshair,
  FileCode,
} from 'lucide-vue-next';
import type { QuickAddType } from '@/stores/quickAddStore';
import { useKeyboardShortcut } from '@/composables/useKeyboardShortcut';

const emit = defineEmits<{
  select: [type: QuickAddType];
  cancel: [];
}>();

const authStore = useAuthStore();
const isCwrStaff = computed(() => authStore.user?.orgSlug === 'admin');

interface PickerItem {
  type: QuickAddType;
  label: string;
  hint: string;
  shortcut: string;
  icon: typeof Fingerprint;
  /** Low-value items for the current audience render dimmed + below the divider. */
  lowValue?: boolean;
}

const tenantItems: PickerItem[] = [
  { type: 'note',      label: 'Note',            hint: 'Attach a note to an existing artifact', shortcut: 'n', icon: MessageSquare },
  { type: 'indicator', label: 'Indicator / IOC', hint: 'Domain, IP, hash, URL',                 shortcut: 'i', icon: Fingerprint },
  { type: 'rule',      label: 'Detection Rule',  hint: 'Splunk SPL query',                      shortcut: 'r', icon: FileCode },
  { type: 'campaign',  label: 'Campaign',        hint: 'Internal operation tracking',           shortcut: 'c', icon: Crosshair,  lowValue: true },
  { type: 'actor',     label: 'Threat Actor',    hint: 'Private to your org',                   shortcut: 'a', icon: ShieldAlert, lowValue: true },
];

const cwrItems: PickerItem[] = [
  { type: 'note',      label: 'Note',            hint: 'Attach a note to an existing artifact', shortcut: 'n', icon: MessageSquare },
  { type: 'actor',     label: 'Threat Actor',    hint: 'New adversary or group',               shortcut: 'a', icon: ShieldAlert },
  { type: 'campaign',  label: 'Campaign',        hint: 'Coordinated operation',                shortcut: 'c', icon: Crosshair },
  { type: 'indicator', label: 'Indicator / IOC', hint: 'Domain, IP, hash, URL',                shortcut: 'i', icon: Fingerprint },
  { type: 'rule',      label: 'Detection Rule',  hint: 'Splunk SPL query',                     shortcut: 'r', icon: FileCode },
];

const items = computed<PickerItem[]>(() => (isCwrStaff.value ? cwrItems : tenantItems));

const primaryItems = computed(() => items.value.filter((i) => !i.lowValue));
const lowValueItems = computed(() => items.value.filter((i) => i.lowValue));

// Bind keyboard shortcuts to every item — both primary and dimmed still
// reachable via the keyboard, the visual treatment is just about where
// the analyst's eye lands first. Bind against a single canonical list so
// we don't register duplicate handlers.
const shortcutBindings: PickerItem[] = [
  { type: 'note',      label: 'Note',      hint: '', shortcut: 'n', icon: MessageSquare },
  { type: 'indicator', label: 'Indicator', hint: '', shortcut: 'i', icon: Fingerprint },
  { type: 'actor',     label: 'Actor',     hint: '', shortcut: 'a', icon: ShieldAlert },
  { type: 'campaign',  label: 'Campaign',  hint: '', shortcut: 'c', icon: Crosshair },
  { type: 'rule',      label: 'Rule',      hint: '', shortcut: 'r', icon: FileCode },
];
for (const item of shortcutBindings) {
  useKeyboardShortcut({
    key: item.shortcut,
    handler: () => emit('select', item.type),
  });
}
</script>

<template>
  <div class="quick-add-picker flex flex-col gap-3">
    <div class="quick-add-picker-header flex flex-col gap-1">
      <h2 class="text-lg font-semibold text-text-primary">Quick add</h2>
      <p class="text-xs text-text-muted">What do you want to add?</p>
    </div>

    <!-- Primary items -->
    <div class="quick-add-picker-items flex flex-col gap-1">
      <button
        v-for="item in primaryItems"
        :key="item.type"
        class="quick-add-picker-item flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-700 text-left cwr-motion"
        :data-qa-type="item.type"
        @click="emit('select', item.type)"
      >
        <component :is="item.icon" class="quick-add-picker-icon w-4 h-4 text-accent-blue flex-shrink-0" />
        <div class="quick-add-picker-label flex flex-col gap-0.5 flex-1 min-w-0">
          <span class="text-sm text-text-primary">{{ item.label }}</span>
          <span class="text-xs text-text-muted">{{ item.hint }}</span>
        </div>
        <kbd class="quick-add-picker-shortcut text-xs text-text-muted bg-surface-900 rounded px-1.5 py-0.5 font-mono">
          {{ item.shortcut }}
        </kbd>
      </button>
    </div>

    <!-- Divider + dimmed low-value items (tenant view only) -->
    <template v-if="lowValueItems.length > 0">
      <div class="quick-add-picker-divider flex items-center gap-2 pt-1">
        <span class="h-px flex-1 bg-border-default"></span>
        <span class="text-[10px] uppercase tracking-wider text-text-disabled">Private to your org</span>
        <span class="h-px flex-1 bg-border-default"></span>
      </div>
      <div class="quick-add-picker-items flex flex-col gap-1">
        <button
          v-for="item in lowValueItems"
          :key="item.type"
          class="quick-add-picker-item flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-700 text-left cwr-motion opacity-60 hover:opacity-100"
          :data-qa-type="item.type"
          @click="emit('select', item.type)"
        >
          <component :is="item.icon" class="quick-add-picker-icon w-4 h-4 text-text-muted flex-shrink-0" />
          <div class="quick-add-picker-label flex flex-col gap-0.5 flex-1 min-w-0">
            <span class="text-sm text-text-secondary">{{ item.label }}</span>
            <span class="text-xs text-text-disabled">{{ item.hint }}</span>
          </div>
          <kbd class="quick-add-picker-shortcut text-xs text-text-disabled bg-surface-900 rounded px-1.5 py-0.5 font-mono">
            {{ item.shortcut }}
          </kbd>
        </button>
      </div>
    </template>

    <div class="quick-add-picker-footer text-[10px] text-text-disabled pt-1">
      Press <kbd class="font-mono">esc</kbd> to cancel
    </div>
  </div>
</template>
